import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { signInWithGoogle } from '../services/auth';
import { useApp, useErrorHandler } from '../contexts/AppContext';

const LoginForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setUser } = useApp();
  const handleError = useErrorHandler();

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const user = await signInWithGoogle();
      setUser(user);
    } catch (error: any) {
      setError(error.message);
      handleError(error, 'Google 로그인');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        {/* 로고 및 제목 */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
            보안조직 인원 관리
          </Typography>
          <Typography variant="h6" color="text.secondary" mb={2}>
            Security Organization Manager
          </Typography>
          <Typography variant="body1" color="text.secondary">
            안전하고 효율적인 인원 정보 관리
          </Typography>
        </Box>

        {/* 로그인 카드 */}
        <Card 
          sx={{ 
            width: '100%', 
            maxWidth: 400,
            boxShadow: 3,
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h2" textAlign="center" mb={3}>
              로그인
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                backgroundColor: '#4285f4',
                '&:hover': {
                  backgroundColor: '#3367d6',
                },
                '&:disabled': {
                  backgroundColor: '#9e9e9e',
                }
              }}
            >
              {loading ? '로그인 중...' : 'Google로 로그인'}
            </Button>

            <Box mt={3}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                조직 구성원만 접근 가능합니다
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* 추가 정보 */}
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            ⚡ Progressive Web App으로 제작된 고성능 앱
          </Typography>
          <Typography variant="body2" color="text.secondary">
            🔒 Firebase 보안 인증으로 안전하게 보호
          </Typography>
          <Typography variant="body2" color="text.secondary">
            📱 모든 기기에서 동일한 경험 제공
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginForm;
